'use client';
// Building blocks of the v9 website design. Colours, radii and fonts come from the tokens in globals.css.
import Link from 'next/link';
import { useEffect, useRef, type ComponentProps, type ReactNode } from 'react';
import { useLocale } from 'next-intl';
import { Close, Sar, Sparkle } from './icons';
import { cx } from './cx';

export { cx };

/** Current language helpers: pick(ar, en) for API fields that come in both languages. */
export function useLang() {
  const locale = useLocale();
  const isAr = locale !== 'en';
  return {
    locale,
    isAr,
    lang: (isAr ? 'ar' : 'en') as 'ar' | 'en',
    pick: (ar?: string | null, en?: string | null) => (isAr ? ar || en : en || ar) || '',
  };
}

/** Page-width container (the demo's .wrap). */
export function Wrap({ className, children, ...p }: ComponentProps<'div'>) {
  return <div className={cx('v9-wrap', className)} {...p}>{children}</div>;
}

/** A price with the riyal sign; amounts keep their own direction in Arabic text. */
export function Price({ amount, className, decimals }: { amount: number | string | null | undefined; className?: string; decimals?: boolean }) {
  const n = typeof amount === 'number' ? amount : parseFloat(String(amount ?? ''));
  if (!Number.isFinite(n)) return null;
  const text = n.toLocaleString('en-US', {
    minimumFractionDigits: decimals || !Number.isInteger(n) ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return (
    <span dir="ltr" className={cx('inline-flex items-center gap-[.22em] whitespace-nowrap tabular-nums', className)}>
      <Sar className="h-[.9em] w-[.8em] flex-none" />
      <span>{text}</span>
    </span>
  );
}

type PillVariant = 'sun' | 'deep' | 'plum' | 'ghost' | 'line' | 'white' | 'ghostInk';
const PILL: Record<PillVariant, string> = {
  sun: 'bg-sun text-deep shadow-[0_10px_30px_-12px_rgba(240,213,140,.7)] hover:shadow-[0_14px_36px_-10px_rgba(240,213,140,.95)]',
  deep: 'bg-deep text-white shadow-[0_10px_30px_-14px_rgba(57,22,77,.8)]',
  plum: 'bg-plum text-white shadow-[0_14px_30px_-16px_rgba(85,39,109,.9)] hover:bg-deep',
  ghost: 'bg-transparent text-deep shadow-[inset_0_0_0_1.5px_rgba(57,22,77,.18)] hover:shadow-[inset_0_0_0_1.5px_rgba(57,22,77,.45)]',
  ghostInk: 'bg-white/[.07] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,.3)] hover:bg-white/[.14]',
  line: 'bg-white text-deep shadow-[inset_0_0_0_1px_rgb(var(--c-line))] hover:shadow-[inset_0_0_0_1px_rgb(var(--c-plum)/.45)]',
  white: 'bg-white text-deep shadow-card',
};
const PILL_BASE = 'inline-flex min-h-[46px] items-center justify-center gap-2.5 whitespace-nowrap rounded-full px-[22px] py-2 text-[15.5px] font-extrabold leading-[1.4] transition-[transform,box-shadow,background-color,color] duration-300 ease-mila active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 max-lg:min-h-[44px] max-lg:px-4 max-lg:text-[14.5px]';

export function pillClass(variant: PillVariant = 'plum', extra?: string) {
  return cx(PILL_BASE, PILL[variant], extra);
}

export function Pill({ variant = 'plum', className, ...p }: ComponentProps<'button'> & { variant?: PillVariant }) {
  return <button type="button" className={pillClass(variant, className)} {...p} />;
}

export function PillLink({ variant = 'plum', className, ...p }: ComponentProps<typeof Link> & { variant?: PillVariant }) {
  return <Link className={pillClass(variant, className)} {...p} />;
}

/** Small rounded label above a heading, with a gold sparkle. */
export function Eyebrow({ children, ink, className }: { children: ReactNode; ink?: boolean; className?: string }) {
  return (
    <span className={cx(
      'inline-flex min-h-[34px] items-center gap-2 rounded-full px-4 py-1 text-[14px] font-bold leading-[1.5] ltr:text-[12px] ltr:uppercase ltr:tracking-[.14em]',
      ink ? 'bg-white/[.07] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,.2)]' : 'bg-plum text-white',
      className,
    )}>
      <Sparkle className="h-3.5 w-3.5 text-sun" />
      {children}
    </span>
  );
}

/** Section heading with an optional subtitle and an action at the end (the home rails' head). */
export function SectionHead({ title, sub, action, id, className }: { title: ReactNode; sub?: ReactNode; action?: ReactNode; id?: string; className?: string }) {
  return (
    <div className={cx('mb-4 flex items-end justify-between gap-4', className)}>
      <div className="min-w-0">
        <h2 id={id} className="text-[clamp(22px,2.4vw,30px)] font-extrabold leading-[1.3] text-deep">{title}</h2>
        {sub && <small className="mt-0.5 block font-body text-[14px] font-[var(--fw-body)] text-deep/70">{sub}</small>}
      </div>
      {action}
    </div>
  );
}

/** Body copy in the body font (Bukra in Arabic). */
export function Body({ className, ...p }: ComponentProps<'p'>) {
  return <p className={cx('font-body text-[clamp(15.5px,1.2vw,18px)] font-[var(--fw-body)] leading-[var(--lh-b)]', className)} {...p} />;
}

export function useScrollLock(on: boolean) {
  useEffect(() => {
    if (!on) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [on]);
}

export function useEscape(onEscape: (() => void) | null) {
  useEffect(() => {
    if (!onEscape) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onEscape(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onEscape]);
}

/** A sheet that rises from the bottom on phones and sits centred on wider screens. */
export function Sheet({ open, onClose, label, children, className, closeLabel = 'Close' }: {
  open: boolean; onClose: () => void; label: string; children: ReactNode; className?: string; closeLabel?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollLock(open);
  useEscape(open ? onClose : null);
  useEffect(() => { if (open) ref.current?.focus(); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[110] flex items-end justify-center bg-night/60 backdrop-blur-[3px] sm:items-center sm:p-6" onClick={onClose}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={cx('relative max-h-[92svh] w-full overflow-y-auto rounded-t-[28px] bg-paper text-deep shadow-lift outline-none animate-[v9rise_.35s_var(--ease)] sm:max-w-[520px] sm:rounded-[28px]', className)}
      >
        <button type="button" onClick={onClose} aria-label={closeLabel} className="absolute end-3 top-3 z-[3] grid h-11 w-11 place-items-center rounded-full bg-white text-deep shadow-[0_6px_18px_-8px_rgba(57,22,77,.5)]">
          <Close className="h-5 w-5" />
        </button>
        {children}
      </div>
    </div>
  );
}

/** Star + rating, as on venue cards. */
export function Rating({ value, className }: { value: number | null; className?: string }) {
  if (value == null) return null;
  return (
    <span className={cx('inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-[12.5px] font-extrabold text-deep', className)} dir="ltr">
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-gold" fill="currentColor" aria-hidden="true"><path d="m12 2.8 2.8 5.7 6.3.9-4.6 4.4 1.1 6.3L12 17.1l-5.6 3 1.1-6.3L2.9 9.4l6.3-.9L12 2.8Z" /></svg>
      {value.toFixed(1)}
    </span>
  );
}
